import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { userService, authService } from '../../services/firebaseService';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

const SimplePaymentSuccess = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Utiliser une référence pour suivre si l'initialisation a déjà été effectuée
  // Utiliser useRef au lieu de useState pour éviter les re-rendus
  const initializedRef = React.useRef(false);
  const [processCompleted, setProcessCompleted] = useState(false);
  
  useEffect(() => {
    console.log('SimplePaymentSuccess - Initialisation, initialized:', initializedRef.current);
    
    // Empêcher les initialisations multiples
    if (initializedRef.current) {
      console.log('=== PROTECTION: Initialisation déjà effectuée, ignorer ===');
      return;
    }
    
    // Marquer comme initialisé immédiatement
    initializedRef.current = true;
    
    const initializeAuth = async () => {
      try {
        // Vérifier si l'utilisateur est déjà authentifié
        const currentUser = authService.getCurrentUser();
        console.log('Utilisateur authentifié:', !!currentUser);
        
        if (currentUser) {
          // Déjà authentifié, continuer normalement
          setIsAuthenticated(true);
          return currentUser;
        }
        
        // Essayer de récupérer l'ID utilisateur depuis localStorage
        const savedUserId = localStorage.getItem('stripe_auth_user_id');
        console.log('ID utilisateur récupéré du localStorage:', savedUserId);
        
        if (!savedUserId) {
          console.log('Aucun ID utilisateur trouvé dans localStorage, redirection vers /home');
          navigate('/home');
          return null;
        }
        
        // Tenter une réauthentification silencieuse avec l'ID utilisateur
        console.log('Tentative de réauthentification avec ID:', savedUserId);
        
        try {
          // Réauthentifier l'utilisateur avec son ID
          await authService.reauthenticateWithUserId(savedUserId);
          
          // Vérifier à nouveau l'authentification
          const reauthUser = authService.getCurrentUser();
          console.log('Réauthentification réussie:', !!reauthUser);
          
          if (reauthUser) {
            setIsAuthenticated(true);
            return reauthUser;
          } else {
            console.error('Réauthentification échouée');
            navigate('/home');
            return null;
          }
        } catch (authError) {
          console.error('Erreur lors de la réauthentification:', authError);
          navigate('/home');
          return null;
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error);
        navigate('/home');
        return null;
      }
    };
    
    const updateSubscriptionFromPayment = async (currentUser) => {
      try {
        console.log('=== DÉBUT MISE À JOUR ABONNEMENT ===');
        
        // Récupérer les paramètres de l'URL
        console.log('Paramètres URL:', searchParams);
        console.log('État d\'authentification:', !!currentUser);
        
        // Vérifier si le processus a déjà été complété (localStorage nettoyé)
        const storedPlan = localStorage.getItem('selectedPlan');
        console.log('Plan récupéré depuis localStorage:', storedPlan);
        
        // PROTECTION: Si le localStorage est vide, c'est probablement une seconde initialisation
        if (!storedPlan) {
          console.log('=== PROTECTION: localStorage déjà nettoyé, probable double initialisation ===');
          console.log('=== ANNULATION de la mise à jour pour éviter écrasement du plan ===');
          setProcessCompleted(true); // Marquer comme terminé pour éviter d'autres tentatives
          return; // Sortir immédiatement pour éviter toute mise à jour
        }
        
        setLoading(true);
        console.log('Début de la mise à jour de l\'abonnement');
        
        // Récupérer le plan sélectionné depuis les paramètres d'URL
        // Essayer plusieurs sources possibles pour le type de plan
        let planId = searchParams.get('client_reference_id');
        
        // Si pas trouvé, essayer d'autres paramètres que Stripe pourrait envoyer
        if (!planId) {
          planId = searchParams.get('plan') || searchParams.get('type');
        }
        
        // Si toujours pas trouvé, utiliser le plan du localStorage (déjà vérifié comme non null)
        if (!planId) {
          planId = storedPlan;
          console.log('Plan utilisé depuis localStorage:', planId);
        } else {
          console.log('Plan utilisé depuis URL:', planId);
        }
        
        // Normaliser le plan pour éviter les incohérences
        if (planId) {
          const originalPlanId = planId;
          planId = planId.toLowerCase();
          // S'assurer que le plan est l'un des types valides
          if (!['basic', 'premium', 'gala', 'free'].includes(planId)) {
            console.warn(`Type de plan non reconnu: ${originalPlanId}, utilisation du plan basic par défaut`);
            planId = 'basic';
          }
          console.log(`Plan normalisé: ${originalPlanId} -> ${planId}`);
        }
        
        console.log('Plan détecté:', planId);
        
        // Vérifier à nouveau l'authentification
        if (!currentUser) {
          console.error('Utilisateur non authentifié lors de la mise à jour');
          setError('Vous devez être connecté pour mettre à jour votre abonnement.');
          setSuccess(false);
          return;
        }
        
        // Mettre à jour l'abonnement de l'utilisateur dans Firestore
        console.log('Mise à jour de l\'abonnement avec le plan:', planId);
        try {
          // Vérifier l'ID utilisateur
          console.log('ID utilisateur pour mise à jour:', currentUser.uid);
          
          // Utiliser le plan détecté et normalisé
          console.log('Plan utilisé pour la mise à jour:', planId);
          
          // Appeler la mise à jour avec plus de détails de logging
          const result = await userService.updateSubscription(planId);
          console.log('Résultat de la mise à jour:', result);
        } catch (updateError) {
          console.error('Erreur spécifique lors de la mise à jour:', updateError);
          throw updateError; // Relancer l'erreur pour le traitement plus haut
        }
        
        // Récupérer le profil utilisateur mis à jour
        const profileData = await userService.getUserProfile();
        console.log('Profil mis à jour:', profileData.user);
        setUserProfile(profileData.user);
        
        // Nettoyer le localStorage après utilisation
        // Créer un flag pour indiquer que la mise à jour a été effectuée
        localStorage.setItem('subscription_updated', 'true');
        localStorage.setItem('subscription_updated_plan', planId);
        localStorage.setItem('subscription_updated_time', new Date().toISOString());
        
        // Nettoyer les données de sélection de plan, mais garder le flag de mise à jour
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('stripe_auth_user_id');
        
        setSuccess(true);
        setError(null);
        setProcessCompleted(true);
        console.log('=== MISE À JOUR RÉUSSIE ===');
        console.log(`Plan ${planId} enregistré avec succès dans Firestore`);
        console.log('localStorage nettoyé et flag de mise à jour créé');
      } catch (err) {
        console.error('Erreur lors de la mise à jour de l\'abonnement:', err);
        setError('Impossible de mettre à jour votre abonnement. Veuillez contacter le support.');
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };
    
    // Séquence principale
    const main = async () => {
      // 1. Initialiser l'authentification
      const user = await initializeAuth();
      if (!user) return;
      
      // 2. Mettre à jour l'abonnement
      await updateSubscriptionFromPayment(user);
    };
    
    main();
    
    // Rediriger vers le tableau de bord après 10 secondes si le paiement est réussi
    const redirectTimer = setTimeout(() => {
      if (isAuthenticated && success) {
        navigate('/dashboard');
      }
    }, 10000);
    
    return () => clearTimeout(redirectTimer);
  }, [navigate, searchParams, location.pathname, isAuthenticated, success, processCompleted]);
  


  return (
    <Container className="my-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <Card className="shadow-sm">
            <Card.Body className="p-5 text-center">
              {loading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Mise à jour de votre abonnement...</p>
                </div>
              ) : error ? (
                <div className="mb-4">
                  <Alert variant="danger">
                    <Alert.Heading>Une erreur est survenue</Alert.Heading>
                    <p>{error}</p>
                  </Alert>
                  <Button variant="primary" onClick={() => navigate('/dashboard')} className="mt-3">
                    Retour au tableau de bord
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="success-icon mb-4">
                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '5rem' }}></i>
                    </div>
                    <h2 className="mb-3">Paiement réussi !</h2>
                    <p className="lead">
                      Votre paiement a été traité avec succès et votre abonnement a été mis à jour.
                    </p>
                    <Alert variant="success" className="mt-3">
                      Votre compte a été mis à jour avec succès.
                    </Alert>
                  </div>
                  
                  {userProfile && userProfile.subscriptionType !== 'free' && (
                    <div className="alert alert-info mb-4">
                      <h5>Détails de votre abonnement</h5>
                      <p><strong>Type d'abonnement :</strong> {userProfile.subscriptionType}</p>
                      <p><strong>Date de début :</strong> {
                        userProfile.subscriptionStartDate && userProfile.subscriptionStartDate.toDate 
                          ? userProfile.subscriptionStartDate.toDate().toLocaleDateString() 
                          : new Date().toLocaleDateString()
                      }</p>
                      {userProfile.subscriptionEndDate && (
                        <p>
                          <strong>Date d'expiration :</strong> {
                            userProfile.subscriptionEndDate.toDate 
                              ? userProfile.subscriptionEndDate.toDate().toLocaleDateString() 
                              : new Date(userProfile.subscriptionEndDate).toLocaleDateString()
                          }
                        </p>
                      )}
                      <p><strong>Limite de tournois :</strong> {userProfile.tournamentsLimit}</p>
                      <p><strong>Limite d'athlètes :</strong> {userProfile.athletesLimit}</p>
                    </div>
                  )}
                  
                  <p className="mb-4">
                    Vous allez être redirigé vers le tableau de bord dans quelques secondes.
                    <br />
                    Si vous n'êtes pas redirigé automatiquement, cliquez sur le bouton ci-dessous.
                  </p>
                  
                  <div className="d-grid gap-2 col-md-6 mx-auto">
                    <Button variant="primary" onClick={() => navigate('/dashboard')}>
                      Aller au tableau de bord
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default SimplePaymentSuccess;
